import type { ApprovalRequest, ApprovalStatus, ApprovalType } from '../types/approvals.types';
import { formatCurrency } from '@/lib/formatters';

const toUpperStatus = (value: unknown): ApprovalStatus => {
  const raw = String(value || '').trim().toUpperCase();
  if (raw === 'APPROVED' || raw === 'REJECTED' || raw === 'PENDING') return raw;
  return 'PENDING';
};

const metaString = (metadata: Record<string, unknown> | undefined, key: string): string => {
  const value = metadata?.[key];
  return typeof value === 'string' ? value.trim() : value != null ? String(value).trim() : '';
};

/** Short label for long Cloudinary / absolute URLs so mobile approval cards don't overflow. */
const displayUrlLabel = (url: string): string => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const leaf = parts[parts.length - 1] || parsed.hostname;
    return leaf.length > 64 ? `${leaf.slice(0, 61)}…` : leaf;
  } catch {
    const leaf = raw.split('/').filter(Boolean).pop() || raw;
    return leaf.length > 64 ? `${leaf.slice(0, 61)}…` : leaf;
  }
};

const metaNumber = (metadata: Record<string, unknown> | undefined, key: string): string => {
  const value = metadata?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return value.trim();
  return '';
};

/** Prefer product/UPC identity over the generic MCP boilerplate reason. */
const metaReason = (metadata: Record<string, unknown> | undefined): string | undefined => {
  const productName = metaString(metadata, 'product_name');
  const brand = metaString(metadata, 'brand');
  const storeLabel =
    metaString(metadata, 'store_label') ||
    metaString(metadata, 'store_name');
  const productId = metaString(metadata, 'product_id');
  const storeId = metaString(metadata, 'store_id');
  if ((productName || productId) && (storeLabel || storeId)) {
    const productBit = productName
      ? brand
        ? `${brand} · ${productName}`
        : productName
      : `product ${productId}`;
    return `${storeLabel || `Store ${storeId}`} · ${productBit}`;
  }
  if (productName) {
    return brand ? `${brand} · ${productName}` : productName;
  }
  const upc = metaString(metadata, 'upc');
  if (upc) return `UPC ${upc}`;
  const promo = metaString(metadata, 'code');
  if (promo) return `Promo ${promo}`;
  const userId = metaString(metadata, 'userId') || metaString(metadata, 'user_id');
  const amount = metaNumber(metadata, 'amount');
  const amountCents = metaNumber(metadata, 'amountCents');
  if (userId && amount) return `Credit ${formatCurrency(Number(amount))} → user ${userId}`;
  if (userId && amountCents) {
    return `Cash-out ${formatCurrency(Number(amountCents) / 100)} → user ${userId}`;
  }
  const returnId = metaString(metadata, 'return_id');
  if (returnId) return `Bottle return ${returnId}`;
  const candidateId = metaString(metadata, 'candidate_id');
  if (candidateId) return `Candidate ${candidateId}`;
  const storeDraftName = metaString(metadata, 'name');
  if (
    storeDraftName &&
    (metaString(metadata, 'store_type') || metaString(metadata, 'street')) &&
    !productName
  ) {
    return storeDraftName;
  }
  if (productId) return `Product ${productId}`;
  const reason = metaString(metadata, 'reason');
  return reason || undefined;
};

export type McpDetailRow = { label: string; value: string };

/** Labeled proposal fields for Owner review (catalog / UPC / store mapping / etc.). */
export const formatMcpProposalDetails = (
  approval: Pick<ApprovalRequest, 'type' | 'metadata' | 'reason'>
): McpDetailRow[] => {
  const metadata = approval.metadata;
  if (!metadata) return [];
  const type = String(approval.type || '').toLowerCase();
  const rows: McpDetailRow[] = [];
  const push = (label: string, value: string) => {
    if (value) rows.push({ label, value });
  };

  if (type.includes('catalog_sku') || (Array.isArray(metadata?.upcs) && Array.isArray(metadata?.mappings))) {
    push('Product', metaString(metadata, 'product_name') || metaString(metadata, 'product_id'));
    push('Brand', metaString(metadata, 'brand'));
    push('Manufacturer', metaString(metadata, 'manufacturer'));
    push('Flavor', metaString(metadata, 'flavor'));
    push('Category', metaString(metadata, 'category'));
    const sizeValue = metaNumber(metadata, 'size_value');
    const sizeUnit = metaString(metadata, 'size_unit');
    if (sizeValue || sizeUnit) {
      push('Size', [sizeValue, sizeUnit].filter(Boolean).join(' '));
    }
    push('Deposit eligible', metaString(metadata, 'deposit_eligible'));
    push('Customer markup %', metaNumber(metadata, 'customer_markup_percent'));
    const upcs = Array.isArray(metadata.upcs)
      ? metadata.upcs
          .map((row) => {
            if (!row || typeof row !== 'object') return '';
            const upc = metaString(row as Record<string, unknown>, 'upc');
            const condition = metaString(row as Record<string, unknown>, 'condition');
            return condition ? `${upc} (${condition})` : upc;
          })
          .filter(Boolean)
          .join(', ')
      : '';
    push('UPCs', upcs);
    const mappingLines = Array.isArray(metadata.mappings)
      ? metadata.mappings
          .map((row) => {
            if (!row || typeof row !== 'object') return '';
            const m = row as Record<string, unknown>;
            const store =
              metaString(m, 'store_label') || metaString(m, 'store_name') || metaString(m, 'store_id');
            const shelf = metaNumber(m, 'current_retail_price');
            const customer = metaNumber(m, 'suggested_retail_price');
            const temp = metaString(m, 'temperature_class');
            return `${store}: shelf $${shelf} → customer $${customer}${temp ? ` · ${temp}` : ''}`;
          })
          .filter(Boolean)
          .join('\n')
      : '';
    push('Store mappings', mappingLines);
    if (metaString(metadata, 'product_id')) {
      push('Existing SKU', metaString(metadata, 'product_id'));
    }
  }

  if (type.includes('catalog_product') || metaString(metadata, 'product_name')) {
    // Skip generic catalog_product formatting when this is already a bundled SKU draft.
    if (!type.includes('catalog_sku') && !(Array.isArray(metadata?.upcs) && Array.isArray(metadata?.mappings))) {
    push('Product', metaString(metadata, 'product_name'));
    push('Brand', metaString(metadata, 'brand'));
    push('Manufacturer', metaString(metadata, 'manufacturer'));
    push('Flavor', metaString(metadata, 'flavor'));
    push('Category', metaString(metadata, 'category'));
    push('Subcategory', metaString(metadata, 'subcategory'));
    const sizeValue = metaNumber(metadata, 'size_value');
    const sizeUnit = metaString(metadata, 'size_unit');
    if (sizeValue || sizeUnit) {
      push('Size', [sizeValue, sizeUnit].filter(Boolean).join(' '));
    }
    push('Package', metaString(metadata, 'package_type'));
    push('Material', metaString(metadata, 'container_material'));
    push('Pack qty', metaNumber(metadata, 'pack_quantity'));
    push('Deposit eligible', metaString(metadata, 'deposit_eligible'));
    push('Caffeine', metaString(metadata, 'caffeine'));
    push('Sugar free', metaString(metadata, 'sugar_free'));
    push('Seasonal', metaString(metadata, 'seasonal'));
    push('Discontinued', metaString(metadata, 'discontinued'));
    push('Active', metaString(metadata, 'active'));
    }
  }

  if (type.includes('link_upc') || (metaString(metadata, 'upc') && !type.includes('store_mapping'))) {
    push(
      'Product',
      metaString(metadata, 'product_name') || metaString(metadata, 'product_id')
    );
    if (metaString(metadata, 'product_name') && metaString(metadata, 'product_id')) {
      push('Product SKU', metaString(metadata, 'product_id'));
    }
    push('UPC', metaString(metadata, 'upc'));
    push('Barcode type', metaString(metadata, 'barcode_type'));
    push('Condition', metaString(metadata, 'condition'));
    push('Source', metaString(metadata, 'source'));
    push('Confidence', metaNumber(metadata, 'confidence'));
    push('Verified', metaString(metadata, 'verified'));
  }

  if (type.includes('store_mapping') || metaString(metadata, 'retailer_sku')) {
    push(
      'Product',
      metaString(metadata, 'product_name') || metaString(metadata, 'product_id')
    );
    if (metaString(metadata, 'product_name') && metaString(metadata, 'product_id')) {
      push('Product SKU', metaString(metadata, 'product_id'));
    }
    push(
      'Store',
      metaString(metadata, 'store_label') ||
        metaString(metadata, 'store_name') ||
        metaString(metadata, 'store_id')
    );
    if (
      (metaString(metadata, 'store_label') || metaString(metadata, 'store_name')) &&
      metaString(metadata, 'store_id')
    ) {
      push('Store ID', metaString(metadata, 'store_id'));
    }
    push('Retailer SKU', metaString(metadata, 'retailer_sku'));
    push('Retailer UPC', metaString(metadata, 'retailer_upc'));
    push('Current retail', metaNumber(metadata, 'current_retail_price'));
    push('Suggested retail', metaNumber(metadata, 'suggested_retail_price'));
    push('Stock', metaNumber(metadata, 'stock_level'));
    push('Aisle', metaString(metadata, 'aisle_location'));
  }

  if (type.includes('price_change') || type.includes('inventory_override')) {
    push(
      'Product',
      metaString(metadata, 'product_name') || metaString(metadata, 'product_id')
    );
    if (metaString(metadata, 'product_name') && metaString(metadata, 'product_id')) {
      push('Product SKU', metaString(metadata, 'product_id'));
    }
    push(
      'Store',
      metaString(metadata, 'store_label') ||
        metaString(metadata, 'store_name') ||
        metaString(metadata, 'store_id')
    );
    if (
      (metaString(metadata, 'store_label') || metaString(metadata, 'store_name')) &&
      metaString(metadata, 'store_id')
    ) {
      push('Store ID', metaString(metadata, 'store_id'));
    }
    push('New price', metaNumber(metadata, 'new_price'));
    push('New quantity', metaNumber(metadata, 'new_quantity'));
  }

  if (type.includes('product_image') || metaString(metadata, 'image_url')) {
    push('Product SKU', metaString(metadata, 'product_id'));
    const imageUrl = metaString(metadata, 'image_url');
    push('Image', displayUrlLabel(imageUrl) || imageUrl);
    push('Image type', metaString(metadata, 'image_type'));
    push('Primary', metaString(metadata, 'primary'));
  }

  if (type.includes('nutrition') || metaString(metadata, 'calories') || metaString(metadata, 'serving_size')) {
    push('Product SKU', metaString(metadata, 'product_id'));
    push('Serving', metaString(metadata, 'serving_size'));
    push('Calories', metaNumber(metadata, 'calories'));
    push('Sugar', metaNumber(metadata, 'sugar') || metaNumber(metadata, 'sugars_grams'));
    push('Sodium', metaNumber(metadata, 'sodium') || metaNumber(metadata, 'sodium_milligrams'));
    push('Caffeine mg', metaNumber(metadata, 'caffeine_mg'));
  }

  if (type.includes('alias') || metaString(metadata, 'alias_name')) {
    push('Product SKU', metaString(metadata, 'product_id'));
    push('Alias', metaString(metadata, 'alias_name'));
  }

  if (type.includes('product_tag') || metaString(metadata, 'tag_name')) {
    push('Product SKU', metaString(metadata, 'product_id'));
    push('Tag', metaString(metadata, 'tag_name'));
  }

  if (type.includes('create_promotion') || metaString(metadata, 'code') || metaString(metadata, 'promo_code')) {
    push('Promo code', metaString(metadata, 'code') || metaString(metadata, 'promo_code'));
    push('Discount type', metaString(metadata, 'discount_type') || metaString(metadata, 'type'));
    push('Discount value', metaNumber(metadata, 'discount_value') || metaNumber(metadata, 'value'));
    push('Title', metaString(metadata, 'title') || metaString(metadata, 'name'));
  }

  if (type.includes('credit_adjustment') || metaString(metadata, 'userId') || metaString(metadata, 'user_id')) {
    push('User', metaString(metadata, 'userId') || metaString(metadata, 'user_id'));
    push('Amount', metaNumber(metadata, 'amount'));
    push('Reason code', metaString(metadata, 'reasonCode') || metaString(metadata, 'reason_code'));
  }

  if (type.includes('bottle_return') || metaString(metadata, 'return_id')) {
    push('Return ID', metaString(metadata, 'return_id'));
    push('Decision', metaString(metadata, 'decision'));
    push('Note', metaString(metadata, 'note'));
  }

  if (type.includes('receipt_job') || metaString(metadata, 'job_id')) {
    push('Job ID', metaString(metadata, 'job_id'));
    push('Decision', metaString(metadata, 'decision'));
  }

  if (type.includes('receipt_item_match') || metaString(metadata, 'normalized_name')) {
    push('Store', metaString(metadata, 'store_id'));
    push('Receipt name', metaString(metadata, 'normalized_name'));
    push('Product SKU', metaString(metadata, 'product_id'));
    push('UPC', metaString(metadata, 'upc'));
  }

  if (type.includes('map_unmapped') || metaString(metadata, 'unmapped_id')) {
    push('Unmapped ID', metaString(metadata, 'unmapped_id'));
    push('Product SKU', metaString(metadata, 'product_id'));
  }

  if (type.includes('manual_price') || (metaString(metadata, 'price') && metaString(metadata, 'store_id'))) {
    push('Store', metaString(metadata, 'store_id'));
    push('Product SKU', metaString(metadata, 'product_id'));
    push('Price', metaNumber(metadata, 'price'));
    push('UPC', metaString(metadata, 'upc'));
  }

  if (type.includes('quarantine_upc')) {
    push('UPC', metaString(metadata, 'upc'));
  }

  if (type.includes('set_dispatch_hub')) {
    push('Store', metaString(metadata, 'store_id'));
  }

  if (type.includes('store_upsert')) {
    push('Name', metaString(metadata, 'name'));
    push('Store type', metaString(metadata, 'store_type') || metaString(metadata, 'storeType'));
    push('Store number', metaString(metadata, 'store_number') || metaString(metadata, 'storeNumber'));
    push('Phone', metaString(metadata, 'phone'));
    push('Street', metaString(metadata, 'street'));
    push('City', metaString(metadata, 'city'));
    push('State', metaString(metadata, 'state'));
    push('ZIP', metaString(metadata, 'zip'));
    push('Lat', metaNumber(metadata, 'lat'));
    push('Lng', metaNumber(metadata, 'lng'));
    push('Dispatch hub', metaString(metadata, 'is_dispatch_hub') || metaString(metadata, 'isDispatchHub'));
    push('Primary supplier', metaString(metadata, 'is_primary_supplier') || metaString(metadata, 'isPrimarySupplier'));
  }

  if (type.includes('delivery_window') || metaString(metadata, 'window_id')) {
    push('Window ID', metaString(metadata, 'window_id'));
    push('Action', metaString(metadata, 'action'));
    push('Capacity', metaNumber(metadata, 'capacity'));
  }

  if (type.includes('candidate_decision') || metaString(metadata, 'candidate_id')) {
    push('Candidate ID', metaString(metadata, 'candidate_id'));
    push('Decision', metaString(metadata, 'decision'));
    push('Notes', metaString(metadata, 'notes'));
  }

  if (type.includes('ticket_reply') || metaString(metadata, 'ticket_id')) {
    push('Ticket ID', metaString(metadata, 'ticket_id'));
    push('Action', metaString(metadata, 'action'));
    push('Body', metaString(metadata, 'body'));
    push('Status', metaString(metadata, 'status'));
  }

  if (type.includes('topup_campaign') || (metaString(metadata, 'starts_at') && metaString(metadata, 'ends_at'))) {
    push('Name', metaString(metadata, 'name'));
    push('Starts', metaString(metadata, 'starts_at'));
    push('Ends', metaString(metadata, 'ends_at'));
    push('Description', metaString(metadata, 'description'));
  }

  if (type.includes('settings_patch')) {
    const patch = metadata.settings_patch;
    if (patch && typeof patch === 'object') {
      push('Settings keys', Object.keys(patch as Record<string, unknown>).join(', '));
    }
  }

  if (type.includes('order_intervention')) {
    push('Order ID', metaString(metadata, 'order_id'));
    const adjustments = metadata.adjustments;
    if (adjustments != null) {
      push('Adjustments', typeof adjustments === 'string' ? adjustments : JSON.stringify(adjustments));
    }
  }

  if (type.includes('order_assign')) {
    push('Driver ID', metaString(metadata, 'driver_id'));
    push('Order ID', metaString(metadata, 'order_id'));
  }

  if (type.includes('catalog_csv_import')) {
    push('CSV bytes', metaNumber(metadata, 'csv_bytes'));
    push('Auto promote', metaString(metadata, 'auto_promote'));
    push('Store', metaString(metadata, 'store_id'));
    push('CSV preview', metaString(metadata, 'csv_preview'));
  }

  // Deduplicate by label (first wins) while keeping order.
  const seen = new Set<string>();
  const unique = rows.filter((row) => {
    if (seen.has(row.label)) return false;
    seen.add(row.label);
    return true;
  });

  if (unique.length === 0) {
    const reason = metaString(metadata, 'reason') || (typeof approval.reason === 'string' ? approval.reason : '');
    if (reason) unique.push({ label: 'Summary', value: reason });
  } else {
    const reason = metaString(metadata, 'reason');
    if (reason) unique.push({ label: 'Agent note', value: reason });
  }

  if (type.includes('catalog_product') && !type.includes('catalog_sku') && !metaString(metadata, 'store_id') && !Array.isArray(metadata?.mappings)) {
    unique.push({
      label: 'Store',
      value: 'Not in this draft — use propose_catalog_sku (preferred) or a separate store-mapping proposal'
    });
  }

  return unique;
};

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

export const normalizeApprovalRequest = (raw: unknown): ApprovalRequest | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = String(row.id || row._id || '').trim();
  if (!id) return null;
  const metadata =
    row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : undefined;
  const type = String(row.type || 'UNKNOWN').trim().toUpperCase() as ApprovalType;
  // Display amounts are dollars from metadata (the server row has no top-level amount).
  // amountCents is authoritative only when dollars are absent (wallet_cashout / disputes).
  const dollarCandidate = metadata?.amount ?? metadata?.creditAmount;
  const centsCandidate = metadata?.amountCents ?? metadata?.holdAppliedCents;
  let amount = 0;
  if (typeof dollarCandidate === 'number' && Number.isFinite(dollarCandidate)) {
    amount = dollarCandidate;
  } else if (typeof dollarCandidate === 'string' && dollarCandidate.trim() && Number.isFinite(Number(dollarCandidate))) {
    amount = Number(dollarCandidate);
  } else if (typeof centsCandidate === 'number' && Number.isFinite(centsCandidate)) {
    amount = centsCandidate / 100;
  } else if (typeof centsCandidate === 'string' && centsCandidate.trim() && Number.isFinite(Number(centsCandidate))) {
    amount = Number(centsCandidate) / 100;
  }
  return {
    id,
    type,
    userId: String(row.userId || row.requestedBy || metadata?.userId || 'system'),
    amount,
    orderId:
      typeof row.orderId === 'string'
        ? row.orderId
        : typeof metadata?.orderId === 'string'
          ? metadata.orderId
          : typeof metadata?.order_id === 'string'
            ? metadata.order_id
            : undefined,
    // Prefer identity from metadata (brand/product/UPC) over generic MCP boilerplate.
    reason: metaReason(metadata)
      || (typeof row.reason === 'string' && row.reason.trim() ? row.reason.trim() : undefined)
      || (typeof row.reviewerNotes === 'string' && row.reviewerNotes.trim()
        ? row.reviewerNotes.trim()
        : undefined),
    photoProof: typeof row.photoProof === 'string' ? row.photoProof : undefined,
    status: toUpperStatus(row.status),
    createdAt: optionalString(row.createdAt) || '',
    updatedAt: optionalString(row.updatedAt),
    reviewedAt: optionalString(row.reviewedAt),
    requestedByName: optionalString(row.requestedByName),
    subjectUserName: optionalString(row.subjectUserName),
    reviewedByName: optionalString(row.reviewedByName),
    reviewerNotes: optionalString(row.reviewerNotes),
    entityState: optionalString(row.entityState),
    metadata,
    referenceId:
      typeof row.referenceId === 'string'
        ? row.referenceId
        : row.referenceId != null
          ? String(row.referenceId)
          : undefined
  };
};

export const normalizeApprovalList = (payload: unknown): ApprovalRequest[] => {
  if (!payload || typeof payload !== 'object') return [];
  const root = payload as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === 'object' && !Array.isArray(root.data)
      ? (root.data as Record<string, unknown>)
      : null;
  const source = Array.isArray(root.approvals)
    ? root.approvals
    : Array.isArray(root.data)
      ? root.data
      : Array.isArray(nested?.approvals)
        ? nested.approvals
        : [];
  return source
    .map((row) => normalizeApprovalRequest(row))
    .filter((row): row is ApprovalRequest => Boolean(row));
};

export const isMcpApproval = (approval: ApprovalRequest): boolean =>
  String(approval.type || '').toLowerCase().startsWith('mcp_');

export const formatApprovalHeadline = (approval: ApprovalRequest): string => {
  const type = String(approval.type || 'APPROVAL').replace(/_/g, ' ');
  if (!isMcpApproval(approval)) return type;
  return `AI DRAFT · ${type.replace(/^MCP\s+/i, '')}`;
};
